import { Tabs, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import OrderList from '../List/BatchDetail/OrderList';
import PickingList from '../List/BatchDetail/PickingList';
import BatchBreadCrumb from '../List/BatchDetail/BatchBreadCrumb';
import { useSelector } from '@redux';
import { DynamicObjectType } from 'types/commons';
import { useGetFilter } from 'hooks/useSearchParamsObj';
import { Link } from 'react-router-dom';
import { formatDateTime, formatMoney } from 'utils/formattedString';
import { sumBy, upperFirst } from 'lodash';
import { ORDER_TYPE_CONST, PAYMENT_STATUS } from 'constants/constants';
import { IRestColumnProps } from 'components/rest/Table/TableColumn';
import { OrderStatusLabel } from 'components/commons/StatusLabel';

const BatchDetail = () => {
  const { t } = useTranslation();

  const { searchParams, setSearchParams } = useGetFilter();

  const currentBatch = useSelector<DynamicObjectType>(
    (state) => state.orderBatches.currentData,
  );

  const packingColumns: IRestColumnProps[] = [
    {
      name: 'orderNumber',
      title: 'Order ID',
      render: (d: string, record: DynamicObjectType) => (
        <Link to={`/order-management/${record?.id}/show`}>
          <Typography.Paragraph
            ellipsis={{ rows: 1 }}
            style={{ width: 200, color: '#AB2328' }}
          >
            {d}
          </Typography.Paragraph>
        </Link>
      ),
      isDefault: true,
    },
    {
      name: 'createdAt',
      title: 'Created Time',
      render: (d) => (
        <p style={{ minWidth: 150 }}>{d ? formatDateTime(d) : ''}</p>
      ),
      width: 150,
      isDefault: true,
    },
    {
      name: 'statusOms',
      title: t('orders.status'),
      render: (d, record) => (
        <div style={{ minWidth: 150 }}>
          <OrderStatusLabel record={record} />
        </div>
      ),
      isDefault: true,
    },
    {
      name: 'items',
      title: 'Quantity',
      render: (d: DynamicObjectType[]) => <p>{sumBy(d, 'quantity') || 0}</p>,
      isDefault: true,
    },
    {
      name: 'total',
      title: t('orders.total'),
      render: (d) => <div style={{ minWidth: 120 }}>{formatMoney(d)}</div>,
      isDefault: true,
      width: 120,
    },
    {
      name: 'saleOrderId',
      title: t('orders.saleOrderId'),
      render: (d: string) => <div className="min-w-[150px]">{d}</div>,
      isDefault: true,
    },
    {
      name: 'paymentMethod',
      title: t('orders.paymentMethod'),
      render: (d, rc: DynamicObjectType) => (
        <div style={{ minWidth: 120 }}>
          {upperFirst(rc?.payments?.[0]?.type)}
        </div>
      ),
      isDefault: true,
      width: 120,
    },
    {
      name: 'paymentStatus',
      title: t('orders.paymentStatus'),
      render: (dt: string) => (
        <p style={{ minWidth: 120, color: PAYMENT_STATUS[dt]?.color }}>
          {t(PAYMENT_STATUS[dt]?.text)}
        </p>
      ),
      isDefault: true,
      width: 120,
    },
    {
      name: 'channel',
      title: t('orders.channel'),
      render: (d: string) => <p>{upperFirst(d)}</p>,
      isDefault: true,
    },
    {
      name: 'deliveryCode',
      title: t('orders.shippingNo'),
      render: (dt, rc) => {
        return <div className="min-w-[150px]">{rc?.deliveryCode}</div>;
      },
    },
    {
      name: 'partner',
      title: t('orders.dPartner'),
      render: (dt: DynamicObjectType, rc: DynamicObjectType) => {
        return <>{rc?.shippingCarrier.name}</>;
      },
    },
    {
      name: 'wspId',
      title: t('orders.wspId'),
      isDefault: true,
      render: (dt: string) => {
        return <div className="min-w-[150px]">{dt}</div>;
      },
    },
    {
      name: 'note',
      title: t('orders.note'),
      render: (d: string) => <p className="min-w-[150px]">{upperFirst(d)}</p>,
    },
    {
      name: 'orderType',
      title: t('orders.type'),
      render: (d: DynamicObjectType, rc: DynamicObjectType) => (
        <p>{t(ORDER_TYPE_CONST[rc?.orderType]?.text || '')}</p>
      ),
    },
  ];

  const DETAIL_TABS = [
    {
      text: 'Order List',
      value: 'orders',
      components: searchParams.get('tab') !== 'picking' && currentBatch?.id && (
        <OrderList
          currentData={currentBatch}
          initialFilter={{
            filter: `deliveryCode||$isnull`,
            filters: [
              {
                condition: 'and',
                key: 'statusOms',
                operator: 'ne',
                value: 'CANCELLED',
              },
            ],
          }}
        />
      ),
    },
    {
      text: 'Picking List',
      value: 'picking',
      components: searchParams.get('tab') === 'picking' && currentBatch?.id && (
        <PickingList currentData={currentBatch} />
      ),
    },
  ];

  const handleChangeTabs = (tab) => {
    searchParams.delete('q');
    searchParams.set('tab', tab);
    setSearchParams(searchParams);
  };

  return (
    <>
      <BatchBreadCrumb />
      {searchParams.get('status') !== 'PACKING' ? (
        <Tabs
          activeKey={searchParams.get('tab') || 'orders'}
          onChange={handleChangeTabs}
        >
          {DETAIL_TABS.map((e) => (
            <Tabs.TabPane
              tab={t(e.text)}
              key={e.value}
              tabKey={e.value}
              children={e?.components}
            />
          ))}
        </Tabs>
      ) : (
        currentBatch?.id && (
          <OrderList
            currentData={currentBatch}
            columns={packingColumns}
            initialFilter={{
              filter: `deliveryCode||$notnull`,
              filters: [
                {
                  condition: 'and',
                  key: 'statusOms',
                  operator: 'ne',
                  value: 'CANCELLED',
                },
              ],
            }}
          />
        )
      )}
    </>
  );
};

export default BatchDetail;
